/** Radio call numbers: who has which now, who had it on a date, each person's history; change, add past, import. */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { EmptyState } from './common.tsx';

const d = (s: string | null) => (s ? new Date(`${s}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'now');
const SOURCE: Record<string, string> = { bluehq: 'Changed in BlueHQ', history: 'Added by hand', import: 'Imported', start: 'On file when the log started' };

function Results({ rows, canManage, onChanged }: { rows: any[]; canManage: boolean; onChanged: () => void }) {
  const [fix, setFix] = useState<any>(null);
  const a = useAction();
  if (!rows.length) return <p className="muted">No one on record.</p>;
  return (
    <>
      <table className="table compact">
        <thead>
          <tr>
            <th>Call #</th>
            <th>Who</th>
            <th>From</th>
            <th>To</th>
            <th>Note</th>
            {canManage && <th />}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="mono">
                <strong>{r.call_number}</strong>
              </td>
              <td>
                {r.name}
                {!r.user_id && <span className="small muted"> (not in BlueHQ)</span>}
              </td>
              <td>{d(r.starts_on)}</td>
              <td>{r.ends_on ? d(r.ends_on) : <Badge tone="ok">Now</Badge>}</td>
              <td className="small muted">
                {r.note ?? ''} {r.source !== 'bluehq' ? `· ${SOURCE[r.source]}` : ''}
                {r.entered_by ? ` · ${r.entered_by}` : ''}
              </td>
              {canManage && (
                <td>
                  <button type="button" className="btn ghost sm" onClick={() => setFix({ id: r.id, startsOn: r.starts_on, endsOn: r.ends_on ?? '', note: r.note ?? '' })}>
                    Correct
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {fix && (
        <div className="stack sm cn-fix">
          <div className="small strong">Correct this entry (the old dates are kept in the audit log)</div>
          <div className="grid-3">
            <label>
              From
              <input type="date" value={fix.startsOn} onChange={(e) => setFix({ ...fix, startsOn: e.target.value })} />
            </label>
            <label>
              To (blank = still has it)
              <input type="date" value={fix.endsOn} onChange={(e) => setFix({ ...fix, endsOn: e.target.value })} />
            </label>
            <label>
              Note
              <input value={fix.note} onChange={(e) => setFix({ ...fix, note: e.target.value })} />
            </label>
          </div>
          <div className="row gap-sm">
            <button type="button" className="btn primary sm" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/call-numbers/${fix.id}`, { startsOn: fix.startsOn, endsOn: fix.endsOn || null, note: fix.note || null }), setFix(null), onChanged()))}>
              Save
            </button>
            <button type="button" className="btn ghost sm" onClick={() => setFix(null)}>
              Cancel
            </button>
          </div>
          <ErrorBox error={a.error} />
        </div>
      )}
    </>
  );
}

export function CallNumbersPage() {
  const cur = useLoad(() => api.get<any>('/api/call-numbers'));
  const [num, setNum] = useState('');
  const [date, setDate] = useState('');
  const [person, setPerson] = useState('');
  const [q, setQ] = useState<string | null>(null);
  const res = useLoad(() => (q ? api.get<any>(`/api/call-numbers/search?${q}`) : Promise.resolve(null)), [q]);
  const [tab, setTab] = useState<'now' | 'past' | 'import'>('now');
  const [chg, setChg] = useState<{ userId: string; name: string; callNumber: string; effectiveOn: string; note: string } | null>(null);
  const [past, setPast] = useState({ callNumber: '', who: '', personName: '', startsOn: '', endsOn: '', note: '' });
  const [csv, setCsv] = useState('');
  const [imp, setImp] = useState<any>(null);
  const a = useAction();
  if (!cur.data) return cur.error ? <ErrorBox error={cur.error} /> : <Spinner />;
  const { people, canManage, today } = cur.data;
  const reload = () => (cur.reload(), res.reload());
  return (
    <div className="ops stack">
      <PageHeader crumb="Agency" title="Radio call numbers" sub="Who has which call number, and who had it before. Every change is kept with its dates." />
      <Card title="Look it up">
        <form
          className="cn-search"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(new URLSearchParams({ ...(num ? { number: num } : {}), ...(date ? { date } : {}), ...(person ? { userId: person } : {}) }).toString());
          }}
        >
          <label>
            Who was call number
            <input value={num} onChange={(e) => setNum(e.target.value)} placeholder="104" className="mono" />
          </label>
          <label>
            on
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <span className="cn-or">or</span>
          <label>
            Everything for
            <select value={person} onChange={(e) => setPerson(e.target.value)}>
              <option value="">a person…</option>
              {people.map((p: any) => (
                <option key={p.user_id} value={p.user_id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <button className="btn primary" disabled={!num && !person}>
            Search
          </button>
        </form>
        <p className="small muted">Leave the date blank to see everyone who has ever had that number.</p>
        {res.error ? <ErrorBox error={res.error} /> : res.data ? <Results rows={res.data.results} canManage={canManage} onChanged={reload} /> : null}
      </Card>

      <div className="seg-ctl" role="tablist" aria-label="View">
        <button type="button" role="tab" aria-selected={tab === 'now'} className={tab === 'now' ? 'on' : ''} onClick={() => setTab('now')}>
          Current
        </button>
        {canManage && (
          <>
            <button type="button" role="tab" aria-selected={tab === 'past'} className={tab === 'past' ? 'on' : ''} onClick={() => setTab('past')}>
              Add a past assignment
            </button>
            <button type="button" role="tab" aria-selected={tab === 'import'} className={tab === 'import' ? 'on' : ''} onClick={() => setTab('import')}>
              Import old records
            </button>
          </>
        )}
      </div>
      <ErrorBox error={a.error} />

      {tab === 'now' && (
        <Card title="Who has which call number now">
          {chg && (
            <div className="stack sm cn-fix">
              <div className="small strong">Change {chg.name}’s call number</div>
              <div className="grid-3">
                <label>
                  New call number (blank = none)
                  <input className="mono" value={chg.callNumber} onChange={(e) => setChg({ ...chg, callNumber: e.target.value })} />
                </label>
                <label>
                  Starting
                  <input type="date" max={today} value={chg.effectiveOn} onChange={(e) => setChg({ ...chg, effectiveOn: e.target.value })} />
                </label>
                <label>
                  Why (optional)
                  <input value={chg.note} onChange={(e) => setChg({ ...chg, note: e.target.value })} placeholder="Promoted to sergeant" />
                </label>
              </div>
              <p className="small muted">The old number ends the day before. If someone else has the new number, theirs ends too.</p>
              <div className="row gap-sm">
                <button className="btn primary sm" disabled={a.busy} onClick={() => a.run(async () => (await api.post('/api/call-numbers/assign', { userId: chg.userId, callNumber: chg.callNumber.trim() || null, effectiveOn: chg.effectiveOn, note: chg.note || undefined }), setChg(null), reload()))}>
                  Save
                </button>
                <button className="btn ghost sm" onClick={() => setChg(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {people.length === 0 ? (
            <EmptyState icon="radio" title="No one yet" />
          ) : (
            <table className="table compact">
              <thead>
                <tr>
                  <th>Call #</th>
                  <th>Name</th>
                  <th>Unit</th>
                  <th>Since</th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {people.map((p: any) => (
                  <tr key={p.user_id}>
                    <td className="mono">
                      <strong>{p.call_number ?? '—'}</strong>
                    </td>
                    <td>
                      {p.name}
                      {p.title ? <span className="small muted"> · {p.title}</span> : null}
                    </td>
                    <td>{p.unit ?? '—'}</td>
                    <td>{p.since ? d(p.since) : '—'}</td>
                    {canManage && (
                      <td>
                        <button className="btn sm" onClick={() => setChg({ userId: p.user_id, name: p.name, callNumber: p.call_number ?? '', effectiveOn: today, note: '' })}>
                          Change
                        </button>{' '}
                        <button className="btn ghost sm" onClick={() => (setPerson(p.user_id), setNum(''), setDate(''), setQ(`userId=${p.user_id}`), window.scrollTo({ top: 0, behavior: 'smooth' }))}>
                          History
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === 'past' && canManage && (
        <Card title="Add a past assignment">
          <p className="small">For call numbers from before BlueHQ, including people who no longer work here.</p>
          <div className="grid-3">
            <label>
              Call number
              <input className="mono" value={past.callNumber} onChange={(e) => setPast({ ...past, callNumber: e.target.value })} />
            </label>
            <label>
              Person
              <select value={past.who} onChange={(e) => setPast({ ...past, who: e.target.value })}>
                <option value="">Someone not in BlueHQ (type the name)</option>
                {people.map((p: any) => (
                  <option key={p.user_id} value={p.user_id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            {!past.who && (
              <label>
                Name
                <input value={past.personName} onChange={(e) => setPast({ ...past, personName: e.target.value })} placeholder="Ray Whitlock" />
              </label>
            )}
            <label>
              From
              <input type="date" value={past.startsOn} onChange={(e) => setPast({ ...past, startsOn: e.target.value })} />
            </label>
            <label>
              To
              <input type="date" value={past.endsOn} onChange={(e) => setPast({ ...past, endsOn: e.target.value })} />
            </label>
            <label>
              Note
              <input value={past.note} onChange={(e) => setPast({ ...past, note: e.target.value })} placeholder="Retired" />
            </label>
          </div>
          <div>
            <button
              className="btn primary"
              disabled={a.busy || !past.callNumber || !past.startsOn || !past.endsOn || (!past.who && !past.personName)}
              onClick={() =>
                a.run(async () => {
                  await api.post('/api/call-numbers/history', { callNumber: past.callNumber, userId: past.who || undefined, personName: past.who ? undefined : past.personName, startsOn: past.startsOn, endsOn: past.endsOn, note: past.note || undefined });
                  setPast({ callNumber: '', who: '', personName: '', startsOn: '', endsOn: '', note: '' });
                  reload();
                })
              }
            >
              Add
            </button>
          </div>
        </Card>
      )}

      {tab === 'import' && canManage && (
        <Card title="Import old records">
          <p className="small">
            A spreadsheet saved as <strong>CSV</strong> with columns for the <strong>call number</strong>, the <strong>name</strong>, the <strong>start date</strong> and the <strong>end date</strong>. Names that match someone in BlueHQ are linked to them; others are kept by name.
          </p>
          <input type="file" accept=".csv,text/csv" aria-label="CSV file" onChange={async (e) => (setCsv((await e.target.files?.[0]?.text()) ?? ''), setImp(null))} />
          <div className="row gap-sm">
            <button className="btn" disabled={!csv || a.busy} onClick={() => a.run(async () => setImp(await api.post('/api/call-numbers/import', { csv, preview: true })))}>
              Check it first
            </button>
            <button className="btn primary" disabled={!imp?.preview || a.busy} onClick={() => a.run(async () => (setImp(await api.post('/api/call-numbers/import', { csv })), reload()))}>
              Import
            </button>
          </div>
          {imp && (
            <div className="stack sm">
              <Notice tone={imp.problems.length ? 'warn' : 'ok'}>
                {imp.added} {imp.preview ? 'will be added' : 'added'}. {imp.problems.length ? `${imp.problems.length} row(s) can’t be used:` : ''}
              </Notice>
              {imp.problems.length > 0 && (
                <table className="table compact">
                  <tbody>
                    {imp.problems.map((p: any) => (
                      <tr key={p.row}>
                        <td>Row {p.row}</td>
                        <td>{p.reason}</td>
                        <td className="small muted">{p.text}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
